/**
 * Database Hero Component
 * 
 * Consistent hero section for all database pages.
 * Integrates type navigation tabs at the bottom to reduce visual layering.
 * Premium styling with ambient glows, gold accent, and ornate divider.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DATABASE_TAB_GROUPS } from './DatabaseNav';

function DatabaseHero({
    icon: Icon,
    label,
    title,
    highlightWord,
    description,
    stats = [],
    loading = false,
    showNav = true,
    className = ''
}) {
    const location = useLocation();
    const currentPath = location.pathname;

    // Split title to highlight a word if provided
    const renderTitle = () => {
        if (!highlightWord || !title.includes(highlightWord)) {
            return <span>{title}</span>;
        }
        const parts = title.split(highlightWord);
        return (
            <>
                {parts[0]}
                <span className="text-[#C4A35A]">{highlightWord}</span>
                {parts[1]}
            </>
        );
    };

    return (
        <div
            className={`mb-8 relative overflow-hidden bg-[#0A0A0A]/80 border border-wine-800/30 p-8 md:p-12 ${className}`}
        >
            {/* Ambient background glows */}
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#C4A35A]/8 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-wine-700/15 blur-3xl pointer-events-none" />

            <div className="relative z-10">
                {/* Label with gold accent line */}
                <div className="flex items-center gap-2 mb-4">
                    <div className="h-px w-8 bg-[#C4A35A]" />
                    <span className="text-[#C4A35A] font-bold text-xs uppercase tracking-[0.2em]">
                        {label}
                    </span>
                </div>

                {/* Icon + Title */}
                <div className="flex items-center gap-3 mb-4">
                    {Icon && <Icon className="w-8 h-8 text-[#C4A35A]" />}
                    <h1 className="text-3xl md:text-4xl font-fantasy font-semibold text-cream/95 tracking-wide">
                        {renderTitle()}
                    </h1>
                </div>

                {/* Gold Ornate Divider */}
                <div className="ornate-divider w-32 mb-6" />

                {/* Description */}
                <p className="text-lg text-cream/60 max-w-2xl mb-6">
                    {description}
                </p>

                {/* Stats Row */}
                {stats.length > 0 && (
                    <div className="flex flex-wrap gap-6 mb-6">
                        {stats.map((stat, idx) => (
                            <React.Fragment key={stat.label}>
                                {idx > 0 && (
                                    <div className="w-px h-12 bg-wine-900/30" />
                                )}
                                <div className="flex flex-col">
                                    <span className="text-2xl font-bold text-[#C4A35A]">
                                        {loading ? (
                                            <span className="inline-block w-8 h-7 bg-wine-900/30 rounded animate-pulse" />
                                        ) : (
                                            stat.value
                                        )}
                                    </span>
                                    <span className="text-[10px] text-cream/40 uppercase font-bold tracking-wider">
                                        {stat.label}
                                    </span>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {/* Integrated Type Navigation */}
                {showNav && (
                    <div className="pt-4 border-t border-wine-800/20">
                        <div className="flex flex-wrap gap-x-8 gap-y-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-wine-800/50 scrollbar-track-transparent">
                            {DATABASE_TAB_GROUPS.map((group, groupIdx) => (
                                <div key={groupIdx} className="flex flex-col gap-2 min-w-max">
                                    <span className="text-[10px] text-[#C4A35A] font-bold uppercase tracking-[0.1em] pl-1 opacity-60">
                                        {group.label}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        {group.tabs.map(({ path, label: tabLabel, icon: TabIcon, matchPaths }) => {
                                            const isActive = matchPaths?.some(p => currentPath === p || currentPath.startsWith(p + '/')) ||
                                                (path === '/browse' && currentPath === '/');

                                            return (
                                                <Link
                                                    key={path}
                                                    to={path}
                                                    className={`relative flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium transition-all whitespace-nowrap shrink-0 rounded ${isActive
                                                        ? 'text-[#C4A35A] bg-[#C4A35A]/10'
                                                        : 'text-cream/40 hover:text-cream/70 hover:bg-cream/5'
                                                        }`}
                                                >
                                                    <TabIcon className={`w-3.5 h-3.5 ${isActive ? 'text-[#C4A35A]' : ''}`} />
                                                    <span className="hidden sm:inline">{tabLabel}</span>
                                                    <span className="sm:hidden">{tabLabel.split(' ')[0]}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DatabaseHero;
